import { RouteObject } from 'react-router-dom';
import { CoreBox, CoreTextEllipsis, CoreTypography } from '@youscience/core';
import { QueryClient } from '@tanstack/react-query';
import { getGroupCrumb, getTenantCrumb } from '@stores/breadCrumbStore';
import BreadcrumbLink from '@components/layout/Breadcrumbs/BreadcrumbLink';
import { GROUPS_ROUTES, ORGANIZATION_ROUTES } from '.';
import ErrorPage from '../error-page';
import { commonTenantBreadCrumbs } from './commonBreadcrumbs';

const queryClient = new QueryClient();

export const groupsRoutes: RouteObject[] = [
  {
    path: `${GROUPS_ROUTES.ROOT_ROUTE}/${GROUPS_ROUTES.GROUPS_MANAGEMENT}`,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        async lazy() {
          const { GroupsManagement } = await import('@features/groups/GroupsManagement/GroupsManagement');

          return { Component: GroupsManagement };
        },
        errorElement: <ErrorPage />,
        async loader() {
          const { tagsLoader } = await import('@features/groups/loaders');

          return tagsLoader(queryClient);
        },
        handle: {
          crumb: () => {
            const crumbTenant = getTenantCrumb();
            const tenantId = crumbTenant?.tenantId ?? '';
            const tenantName = crumbTenant?.name ?? 'Details';

            return [
              ...commonTenantBreadCrumbs(tenantId, tenantName),
              <CoreTypography color='text.secondary'>Groups management</CoreTypography>,
            ];
          },
        },
      },
      {
        path: `${GROUPS_ROUTES.GROUP_DETAILS}`,
        async lazy() {
          const { GroupDetail } = await import('@features/groups/GroupDetail/GroupDetail');

          return { Component: GroupDetail };
        },
        errorElement: <ErrorPage />,
        async loader() {
          const { tagDetailsLoader } = await import('@features/groups/loaders');

          return tagDetailsLoader(queryClient);
        },
        handle: {
          crumb: () => {
            // TODO: need to configure a store or use react-router context to pass the tenantName
            const crumbTenant = getTenantCrumb();
            const groupCrumb = getGroupCrumb();

            const tenantId = crumbTenant?.tenantId ?? '';
            const tenantName = crumbTenant?.name ?? 'Details';
            const tagName = groupCrumb?.name ?? 'Tag Name';

            return [
              ...commonTenantBreadCrumbs(tenantId, tenantName),
              <BreadcrumbLink
                to={`/${ORGANIZATION_ROUTES.ORGANIZATIONS}/${tenantId}/${GROUPS_ROUTES.GROUPS_MANAGEMENT}`}
              >
                Groups management
              </BreadcrumbLink>,

              <CoreBox sx={{ width: '100%', maxWidth: 230 }}>
                <CoreTextEllipsis variant='body1' component='div' value={tagName} symbolLimit={30} />
              </CoreBox>,
            ];
          },
        },
      },
      {
        path: `${GROUPS_ROUTES.BULK_INVITE}`,
        async lazy() {
          const { UsersBulkInvite } = await import('@features/invitations/UsersBulkInvite');

          return { Component: UsersBulkInvite };
        },
        errorElement: <ErrorPage />,
        handle: {
          crumb: () => {
            const crumbTenant = getTenantCrumb();
            const groupCrumb = getGroupCrumb();

            const tenantId = crumbTenant?.tenantId ?? '';
            const tenantName = crumbTenant?.name ?? 'Details';

            return [
              ...commonTenantBreadCrumbs(tenantId, tenantName),
              <BreadcrumbLink
                to={`/${ORGANIZATION_ROUTES.ORGANIZATIONS}/${tenantId}/${GROUPS_ROUTES.GROUPS_MANAGEMENT}`}
              >
                Groups management
              </BreadcrumbLink>,
              <BreadcrumbLink to={`/organizations/${tenantId}/groups-management/${groupCrumb?.tagId ?? ''}`}>
                {groupCrumb?.name ?? 'Group'}
              </BreadcrumbLink>,
              <CoreTypography color='text.secondary'>Bulk Invite</CoreTypography>,
            ];
          },
        },
      },
      {
        path: `${GROUPS_ROUTES.USER_DETAIL_REPORT}`,
        async lazy() {
          const { GroupUserDetailReport } =
            await import('@features/groups/GroupUserDetailReport/GroupUserDetailReport');

          return { Component: GroupUserDetailReport };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { tagUserDetailReportLoader } = await import('@features/groups/loaders');

          return tagUserDetailReportLoader({ request, params });
        },
        handle: {
          crumb: () => {
            const crumbTenant = getTenantCrumb();
            const groupCrumb = getGroupCrumb();

            const tenantId = crumbTenant?.tenantId ?? '';
            const tenantName = crumbTenant?.name ?? 'Details';
            const tagName = groupCrumb?.name ?? 'Group details';

            return [
              ...commonTenantBreadCrumbs(tenantId, tenantName),
              <BreadcrumbLink
                to={`/${ORGANIZATION_ROUTES.ORGANIZATIONS}/${tenantId}/${GROUPS_ROUTES.GROUPS_MANAGEMENT}`}
              >
                Groups management
              </BreadcrumbLink>,
              <BreadcrumbLink to={`/organizations/${tenantId}/groups-management/${groupCrumb?.tagId ?? ''}`}>
                {tagName}
              </BreadcrumbLink>,
              <CoreTypography color='text.secondary'>User detail report</CoreTypography>,
            ];
          },
        },
      },
    ],
  },
  {
    path: `${GROUPS_ROUTES.ROOT_ROUTE}/${GROUPS_ROUTES.NEW_GROUP}`,
    async lazy() {
      const { NewGroup } = await import('@features/groups/NewGroup/NewGroup');

      return { Component: NewGroup };
    },
    errorElement: <ErrorPage />,
  },
];
